/**
 * Content Type Query Builder
 * Synchronizes query logic with Velocity template for different content types
 */

import { DEFAULT_LANGUAGE_ID } from './constants';

/**
 * Build base query based on content type and conhost
 * @param {string} contentType - The content type
 * @param {string} conhostId - The conhost ID (optional)
 * @returns {string} Base query string
 */
export const buildBaseQuery = (contentType, conhostId) => {
    if (contentType === 'Announcement') {
        return `languageId:${DEFAULT_LANGUAGE_ID} +live:true`;
    }

    if (contentType === 'Contributor') {
        return `+live:true`;
    }

    if (conhostId) {
        return `+conhost:${conhostId} +languageId:${DEFAULT_LANGUAGE_ID} +live:true`;
    }

    return `+languageId:${DEFAULT_LANGUAGE_ID} +live:true`;
};

/**
 * Build content type specific query
 * @param {string} contentType - The content type
 * @returns {string} Content type specific query string
 */
export const buildContentTypeQuery = (contentType) => {
    const contentQueries = {
        Blog: '+Blog.showInListing:true',
        CaseStudy: '',
        Video: '+Video.show:true',
        Podcast: '+Podcast.show:true',
        Reviews: '+Reviews',
        ResourceLibrary: '+ResourceLibrary',
        Announcement: '+Announcement'
    };

    return contentQueries[contentType] || '';
};

/**
 * Build sort configuration for content type
 * @param {string} contentType - The content type
 * @returns {Object} Sort configuration with field and order
 */
export const buildSortConfig = (contentType) => {
    const sortConfigs = {
        Blog: {
            field: 'Blog.postingDate',
            order: 'desc'
        },
        CaseStudy: {
            field: 'modDate',
            order: 'desc'
        },
        Video: {
            field: 'Video.publishDate',
            order: 'desc'
        },
        Podcast: {
            field: 'Podcast.publishDate',
            order: 'desc'
        },
        Reviews: {
            field: 'modDate',
            order: 'desc'
        },
        ResourceLibrary: {
            field: 'modDate',
            order: 'desc'
        },
        Announcement: {
            field: 'modDate',
            order: 'desc'
        }
    };

    return (
        sortConfigs[contentType] || {
            field: `${contentType}.publishDate`,
            order: 'desc'
        }
    );
};

/**
 * Merge two query strings by finding differences and deduplicating common parts
 * @param {string} baseQuery - Base query string
 * @param {string} additionalQuery - Additional query string
 * @returns {string} Merged query string without duplicates
 */
export const mergeQueries = (baseQuery, additionalQuery) => {
    if (!baseQuery) return additionalQuery;
    if (!additionalQuery) return baseQuery;

    if (baseQuery === additionalQuery) return baseQuery;

    const parseQuery = (query) => {
        return query.trim().split(/\s+/).filter(Boolean);
    };

    const baseParts = parseQuery(baseQuery);
    const additionalParts = parseQuery(additionalQuery);

    const uniqueAdditionalParts = additionalParts.filter((part) => !baseParts.includes(part));

    if (uniqueAdditionalParts.length === 0) {
        return baseQuery;
    }

    const allBasePartsInAdditional = baseParts.every((part) => additionalParts.includes(part));
    if (allBasePartsInAdditional && additionalParts.length > baseParts.length) {
        return additionalQuery;
    }

    return [baseQuery, ...uniqueAdditionalParts].join(' ');
};

/**
 * Build complete query for content type
 * @param {string} contentType - The content type
 * @param {string} conhostId - The conhost ID (optional)
 * @param {string} additionalQuery - Additional query string to append
 * @returns {Object} Complete query configuration
 */
export const buildContentTypeQueryConfig = (contentType, conhostId, additionalQuery = '') => {
    const baseQuery = buildBaseQuery(contentType, conhostId);
    const contentQuery = buildContentTypeQuery(contentType);
    const sortBy = buildSortConfig(contentType);

    const mergedQuery = mergeQueries(baseQuery, additionalQuery);
    const fullQuery = mergeQueries(mergedQuery, contentQuery);

    return {
        query: fullQuery,
        sortBy,
        baseQuery,
        contentQuery
    };
};

/**
 * Calculate limit based on feature post setting
 * @param {boolean} hasFeaturePost - Whether feature post is enabled
 * @param {number} defaultLimit - Default limit (default: 12)
 * @returns {number} Calculated limit
 */
export const calculateLimit = (hasFeaturePost = false, defaultLimit = 12) => {
    return hasFeaturePost ? defaultLimit + 1 : defaultLimit;
};

/**
 * Build category configuration for different content types
 * @param {string} contentType - The content type
 * @param {Array} categories - Categories array
 * @param {string} filterBy - Filter by field
 * @param {string} categoryFieldVariable - Category field variable
 * @returns {Object} Category configuration
 */
export const buildCategoryConfig = (
    contentType,
    categories = [],
    _filterBy = '',
    categoryFieldVariable = ''
) => {
    if (contentType === 'ResourceLibrary') {
        return {
            field: 'categories',
            list: categories.map((category) => ({
                key: category.key,
                categoryName: category.categoryName
            }))
        };
    } else if (contentType === 'Announcement') {
        const typeOptions = categories.map((category) => ({
            key: category.toLowerCase(),
            categoryName: category
        }));

        return {
            field: 'type1',
            list: typeOptions
        };
    } else {
        const childrenCategories = categories.map((cat) => ({
            key: cat.key,
            categoryName: cat.categoryName
        }));

        return {
            field: categoryFieldVariable,
            list: childrenCategories
        };
    }
};
